import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, ArrowUp, ArrowDown, MessageCircle, Plus, X, Lock } from "lucide-react";

const CATEGORIES = [
  "AI / ML", "Computer Science", "Cybersecurity", "IoT",
  "Public Health", "Mental Health", "Climate", "Civil Engineering",
  "Education", "Economics", "Multidisciplinary",
];

const SORTS = [
  { k: "hot", l: "Hot" },
  { k: "new", l: "New" },
  { k: "top", l: "Top" },
];

export default function DiscussionsPublic() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("hot");
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category: CATEGORIES[0] });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/discussions/threads", { params: { sort, ...(category ? { category } : {}) } });
      setThreads(r.data || []);
    } catch { /* keep empty */ }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [sort, category]);

  const canCreate = user && (user.role === "admin" || user.verified);

  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and body required"); return; }
    try {
      const r = await api.post("/discussions/threads", form);
      toast.success("Thread posted");
      setComposing(false); setForm({ title: "", body: "", category: CATEGORIES[0] });
      navigate(`/discussions/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to post"); }
  };

  const vote = async (id, value) => {
    if (!user) { toast.error("Sign in to vote"); return; }
    try {
      const r = await api.post(`/discussions/threads/${id}/vote`, { value });
      setThreads((prev) => prev.map((t) => t.id === id ? { ...t, score: r.data.score, my_vote: r.data.my_vote } : t));
    } catch (e) { toast.error("Vote failed"); }
  };

  return (
    <div className="min-h-screen bg-kb-bg" data-testid="discussions-public-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">RESEARCHER FORUMS</div>
            <h1 className="font-serif text-4xl lg:text-5xl text-kb-ink mt-2 leading-tight">
              Discuss, debate, <em className="italic text-kb-primary">collaborate</em>.
            </h1>
            <p className="mt-3 text-kb-ink-muted max-w-2xl text-sm">
              A Reddit-style space for verified researchers. Ask method questions, share findings,
              find collaborators. Verified users + admins start threads; anyone signed-in can reply and vote.
            </p>
          </div>
          {canCreate && (
            <button onClick={() => setComposing(true)} className="kb-btn-primary text-sm" data-testid="new-thread-btn">
              <Plus className="w-3.5 h-3.5" /> New thread
            </button>
          )}
        </div>

        {composing && (
          <div className="mt-6 kb-card p-6" data-testid="thread-composer">
            <div className="flex items-center justify-between mb-3">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary">NEW THREAD</div>
              <button onClick={() => setComposing(false)} className="text-kb-ink-muted"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <input className="kb-input text-sm w-full" placeholder="Title (e.g. Looking for collaborators on Nepali NLP corpus)"
                value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="thread-title-input" />
              <select className="kb-input text-sm" value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="thread-category-select">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <textarea rows={5} className="kb-input text-sm resize-none w-full"
                placeholder="Body — context, your question, what you've tried, etc."
                value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                data-testid="thread-body-input" />
              <div className="flex gap-2">
                <button onClick={submit} className="kb-btn-primary text-sm" data-testid="thread-submit-btn">Post thread</button>
                <button onClick={() => setComposing(false)} className="kb-btn-outline text-sm">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {!canCreate && user && (
          <div className="mt-6 border border-amber-200 bg-amber-50 rounded-sm p-4 text-sm text-amber-900 flex items-start gap-3" data-testid="verify-cta-banner">
            <Lock className="w-4 h-4 mt-0.5" />
            <div>
              <strong>Verified-user feature.</strong> Get verified by completing your researcher profile or an institution affiliation to start threads. You can still vote and reply.
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-2">
          {SORTS.map((s) => (
            <button key={s.k} onClick={() => setSort(s.k)}
              className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded-sm border ${sort === s.k ? "border-kb-primary bg-kb-primary text-white" : "border-slate-200 bg-white text-kb-ink-muted"}`}
              data-testid={`discussions-sort-${s.k}`}>
              {s.l}
            </button>
          ))}
          <div className="w-px bg-slate-200 mx-1" />
          <button onClick={() => setCategory("")}
            className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded-sm border ${category === "" ? "border-kb-primary bg-kb-primary text-white" : "border-slate-200 bg-white text-kb-ink-muted"}`}>
            ALL
          </button>
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCategory(c)}
              className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded-sm border ${category === c ? "border-kb-primary bg-kb-primary text-white" : "border-slate-200 bg-white text-kb-ink-muted"}`}>
              {c}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-3">
          {loading && <div className="kb-mono text-xs text-kb-ink-muted">Loading…</div>}
          {!loading && threads.length === 0 && (
            <div className="text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">
              No threads yet — be the first to start a conversation.
            </div>
          )}
          {threads.map((t) => (
            <article key={t.id} className="kb-card p-5 flex gap-4" data-testid={`thread-${t.id}`}>
              <div className="flex flex-col items-center gap-1 shrink-0 w-10">
                <button onClick={() => vote(t.id, t.my_vote === 1 ? 0 : 1)}
                  className={`p-1 rounded-sm hover:bg-slate-100 ${t.my_vote === 1 ? "text-kb-primary" : "text-kb-ink-muted"}`}
                  data-testid={`thread-${t.id}-upvote`} aria-label="Upvote">
                  <ArrowUp className="w-4 h-4" />
                </button>
                <div className="kb-mono text-sm text-kb-ink">{t.score || 0}</div>
                <button onClick={() => vote(t.id, t.my_vote === -1 ? 0 : -1)}
                  className={`p-1 rounded-sm hover:bg-slate-100 ${t.my_vote === -1 ? "text-rose-600" : "text-kb-ink-muted"}`}
                  data-testid={`thread-${t.id}-downvote`} aria-label="Downvote">
                  <ArrowDown className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary">{t.category}</span>
                  <span className="kb-mono text-[10px] text-kb-ink-muted">· {t.author_name || "Anonymous"}</span>
                  {t.author_verified && <span className="kb-mono text-[9px] uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm">VERIFIED</span>}
                  <span className="kb-mono text-[10px] text-kb-ink-light">· {new Date(t.created_at).toLocaleDateString()}</span>
                </div>
                <Link to={`/discussions/${t.id}`} className="block font-serif text-xl text-kb-ink mt-1 hover:text-kb-primary leading-snug">
                  {t.title}
                </Link>
                {t.body && <p className="text-sm text-kb-ink-muted mt-2 line-clamp-2">{t.body}</p>}
                <div className="mt-3 flex items-center gap-4 text-xs text-kb-ink-muted">
                  <Link to={`/discussions/${t.id}`} className="inline-flex items-center gap-1 hover:text-kb-primary" data-testid={`thread-${t.id}-view`}>
                    <MessageCircle className="w-3.5 h-3.5" /> {t.reply_count || 0} replies
                  </Link>
                  <Link to={`/discussions/${t.id}`} className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1">
                    Open <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
